import palette from "@/palette";
import { Checkbox, SxProps, Theme, useTheme } from "@mui/material";
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';

export type CheckboxState = "unchecked" | "blue-tick" | "green-tick" | "red-cross"

interface ICheckboxProps {
    checked: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    sx?: SxProps<Theme>;
    indeterminate?: boolean;
    onStateChange?: (currentState: CheckboxState, nextState: CheckboxState) => void;
    currentState?: CheckboxState;
}

export const getNextState = (currentState: CheckboxState): CheckboxState => {
    switch(currentState) {
        case "unchecked":
            return "green-tick";
        case "blue-tick":
            return "green-tick";
        case "green-tick":
            return "red-cross";
        case "red-cross":
            return "unchecked";
    }
};

export const CustomCheckbox = ({
    indeterminate,
    checked,
    onChange,
    sx,
    onStateChange,
    currentState = "unchecked"
}: ICheckboxProps) => {

    const { palette: themePalette } = useTheme();
    const isDarkMode = themePalette.mode === 'dark';

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!onStateChange) {
            onChange(event, event.target.checked);
            return;
        }

        const nextState = getNextState(currentState);
        onStateChange?.(currentState, nextState);
    };

    const getIcon = () => {
        if (!onStateChange) {
            return undefined;
        }

        switch (currentState) {
            case "blue-tick":
                return <CheckBoxOutlinedIcon sx={{ color: 'info.main' }} />;
            case "green-tick":
                return <CheckBoxOutlinedIcon sx={{ color: 'success.main' }} />;
            case "red-cross":
                return <BlockOutlinedIcon sx={{ color: 'error.main' }} />;
            default:
                return <CheckBoxOutlineBlankOutlinedIcon sx={{ color: 'action.active' }} />;
        }
    };

    return (
        <Checkbox
            indeterminate={indeterminate}
            checked={onStateChange ? currentState !== "unchecked" : checked}
            onChange={handleChange}
            icon={onStateChange ? getIcon() : undefined}
            checkedIcon={getIcon()}
            sx={{
                backgroundColor: !isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                borderRadius: '4px',
                color: !isDarkMode ? palette.dark.grey[800] : palette.dark.grey[400],
                zIndex: 999,
                '&:hover': {
                    backgroundColor: !isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                },
                ...sx
            }}
        />
    );
};